import React from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { FaArrowUp, FaArrowDown, FaSignInAlt, FaSignOutAlt, FaExchangeAlt } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { formatPrice, formatAmount, formatCurrency, parseFloatFromString } from '../utils/formatters';
import DelistedIcon from './DelistedIcon';
import FailedTransactionWarning from './FailedTransactionWarning';
import { tokenRoutePath } from '../utils/tokenRoutes';

const grossCurrencyValue = (transaction) => {
  const value = parseFloatFromString(transaction?.price) * parseFloatFromString(transaction?.quantity);
  return Number.isFinite(value) ? value : 0;
};

const parenthesizeLocation = (label) => label ? ` (${label})` : '';

export const buildTransactionClipboardSummary = (transactionWithToken, tokenLocations = []) => {
  const { transaction, token } = transactionWithToken || {};
  if (!transaction) return '';
  const txType = transaction.transactionType;
  const ticker = token?.ticker || transaction.token?.ticker || '';
  const date = format(new Date(transaction.timestamp), 'dd/MM');
  const quantity = formatAmount(transaction.quantity, '');
  const price = formatPrice(transaction.price, transaction.currency);
  const cost = formatCurrency(transaction.cost || grossCurrencyValue(transaction), transaction.currency);
  const proceeds = formatCurrency(transaction.proceeds || grossCurrencyValue(transaction), transaction.currency);
  const locationName = (id) => {
    if (!id) return null;
    return tokenLocations.find((location) => location.id === id)?.name || id;
  };
  const locationLabel = txType === 'INTERNAL_TRANSFER'
    ? [locationName(transaction.sourceLocationId), locationName(transaction.destinationLocationId)].filter(Boolean).join(' → ')
    : locationName(transaction.locationId);
  const location = parenthesizeLocation(locationLabel);
  const asset = [quantity, ticker].filter(Boolean).join(' ');

  switch (txType) {
    case 'SPLIT':
      return `${date} - ${ticker} stock split ×${transaction.quantity}`;
    case 'BUY':
      return `${date} - ${cost} -> ${asset} @ ${price}${location}`;
    case 'SELL':
      return `${date} - ${asset} @ ${price} -> ${proceeds}${location}`;
    case 'TRANSFER_IN':
      return `${date} - +${asset}${location}`;
    case 'TRANSFER_OUT':
      return `${date} - -${asset}${location}`;
    case 'INTERNAL_TRANSFER':
      return `${date} - ${asset}${location}`;
    default:
      return `${date} - ${asset} @ ${price}${location}`;
  }
};

const copyTextToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);
};

const TransactionListItem = ({ transactionWithToken, onEdit, onDelete, tokenLocations = [] }) => {
  const { transaction, token } = transactionWithToken;
  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd/MM HH:mm');
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getTransactionTypeIcon = (type) => {
    switch (type) {
      case 'BUY':
        return { icon: FaArrowUp, color: 'text-green-500' };
      case 'SELL':
        return { icon: FaArrowDown, color: 'text-red-500' };
      case 'TRANSFER_IN':
        return { icon: FaSignInAlt, color: 'text-blue-500' };
      case 'TRANSFER_OUT':
        return { icon: FaSignOutAlt, color: 'text-orange-500' };
      case 'SPLIT':
      case 'INTERNAL_TRANSFER':
        return { icon: FaExchangeAlt, color: 'text-purple-500' };
      default:
        return { icon: FaArrowUp, color: 'text-gray-500' };
    }
  };

  const locationName = (id) => {
    if (!id) return null;
    return tokenLocations.find((location) => location.id === id)?.name || id;
  };

  const locationLabel = (() => {
    if (transaction.transactionType === 'INTERNAL_TRANSFER') {
      if (!transaction.sourceLocationId && !transaction.destinationLocationId) return null;
      return `${locationName(transaction.sourceLocationId) || 'Unknown'} → ${locationName(transaction.destinationLocationId) || 'Unknown'}`;
    }
    return transaction.locationId ? locationName(transaction.locationId) : null;
  })();

  const handleCopy = async (event) => {
    event.stopPropagation();
    const summary = buildTransactionClipboardSummary(transactionWithToken, tokenLocations);
    try {
      await copyTextToClipboard(summary);
      toast.success('Transaction copied');
    } catch (error) {
      console.error('Failed to copy transaction summary:', error);
      toast.error('Could not copy transaction');
    }
  };

  const calculateTotalValue = (price, quantity) => {
    if (!price || !quantity) return 0;
    const priceNum = parseFloatFromString(price);
    const quantityNum = parseFloatFromString(quantity);
    if (isNaN(priceNum) || isNaN(quantityNum)) return 0;
    return (priceNum * quantityNum).toFixed(0);
  };

  const txType = transaction.transactionType;
  const totalValue = calculateTotalValue(transaction.price, transaction.quantity);
  const valueDisplay = (() => {
    if (txType === 'SPLIT') return `×${transaction.quantity}`;
    if (txType === 'BUY') return formatPrice(totalValue, transaction.currency);
    if (txType === 'SELL') return formatPrice(totalValue, transaction.currency);
    if (txType === 'TRANSFER_IN') return `+${formatAmount(transaction.quantity, '')}`;
    if (txType === 'TRANSFER_OUT') return `-${formatAmount(transaction.quantity, '')}`;
    if (txType === 'INTERNAL_TRANSFER') return formatAmount(transaction.quantity, '');
    return formatPrice(totalValue, transaction.currency);
  })();
  const detailDisplay = (() => {
    if (txType === 'BUY' || txType === 'SELL') {
      return `${formatAmount(transaction.quantity, '')} @ ${formatPrice(transaction.price, transaction.currency)}`;
    }
    return transaction.transactionType?.replace('_', ' ') || 'Transaction';
  })();

  return (
    <>
      <div className="md:hidden border-b border-gray-200 dark:border-gray-700 px-3 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex-shrink-0 w-5 pt-0.5 flex justify-center">
              {(() => {
                const { icon: Icon, color } = getTransactionTypeIcon(transaction.transactionType);
                return <Icon className={`w-4 h-4 ${color}`} title={transaction.transactionType.replace('_', ' ')} />;
              })()}
            </div>
            <div className="min-w-0">
              {token && (
                <div className="text-sm font-medium flex items-center gap-1">
                  <Link
                    to={tokenRoutePath(token)}
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {token.ticker}
                  </Link>
                  {token.isDelisted && <DelistedIcon className="w-3.5 h-3.5" />}
                </div>
              )}
              <div className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                {detailDisplay}
              </div>
              <div className="mt-1 flex max-w-[12rem] items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                {locationLabel && (
                  <span className="truncate text-purple-600 dark:text-purple-400" title={locationLabel}>
                    {locationLabel}
                  </span>
                )}
                {transaction.notes && <span className="truncate">{transaction.notes}</span>}
                <FailedTransactionWarning transaction={transaction} />
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 text-right">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{valueDisplay}</div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{formatDate(transaction.timestamp)}</div>
            <div className="mt-2 flex justify-end gap-2">
              <button
                onClick={handleCopy}
                className="text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100 p-1"
                title="Copy transaction summary"
                aria-label="Copy transaction summary"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onEdit?.(transaction); }}
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 p-1"
                title="Edit transaction"
                aria-label="Edit transaction"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete?.(transaction); }}
                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-1"
                title="Delete transaction"
                aria-label="Delete transaction"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden md:flex items-center justify-between py-3 px-4 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
      <div className="flex items-center space-x-3 flex-1">
        {/* Transaction type icon */}
        <div className="flex-shrink-0 w-6 flex justify-center">
          {(() => {
            const { icon: Icon, color } = getTransactionTypeIcon(transaction.transactionType);
            return <Icon 
              className={`w-4 h-4 ${color}`} 
              title={transaction.transactionType.replace('_', ' ')}
            />;
          })()}
        </div>
        
        {/* Main transaction info */}
        <div className="flex items-center space-x-3 flex-1">
          {/* Token ticker with link - fixed width for alignment */}
          {token && (
            <div className="text-sm font-medium w-16 flex-shrink-0 flex items-center gap-1">
              <Link
                to={tokenRoutePath(token)}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                {token.ticker}
              </Link>
              {token.isDelisted && <DelistedIcon className="w-3.5 h-3.5" />}
            </div>
          )}

          {/* Transaction details based on type */}
          {(() => {
            const totalValue = calculateTotalValue(transaction.price, transaction.quantity);
            
            switch (transaction.transactionType) {
              case 'BUY':
                return (
                  <>
                    <div className="text-sm text-gray-900 dark:text-gray-100 font-medium">
                      {formatPrice(totalValue, transaction.currency)}
                    </div>
                    <div className="text-sm text-gray-400">→</div>
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      {formatAmount(transaction.quantity, '')} @ {formatPrice(transaction.price, transaction.currency)}
                    </div>
                  </>
                );
              case 'SELL':
                return (
                  <>
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      {formatAmount(transaction.quantity, '')} @ {formatPrice(transaction.price, transaction.currency)}
                    </div>
                    <div className="text-sm text-gray-400">→</div>
                    <div className="text-sm text-gray-900 dark:text-gray-100 font-medium">
                      {formatPrice(totalValue, transaction.currency)}
                    </div>
                  </>
                );
              case 'TRANSFER_IN':
                return (
                  <div className="text-sm text-green-600 dark:text-green-400 font-medium">
                    +{formatAmount(transaction.quantity, '')}
                  </div>
                );
              case 'TRANSFER_OUT':
                return (
                  <div className="text-sm text-red-600 dark:text-red-400 font-medium">
                    -{formatAmount(transaction.quantity, '')}
                  </div>
                );
              case 'INTERNAL_TRANSFER':
                return (
                  <div className="text-sm text-purple-600 dark:text-purple-400 font-medium">
                    {formatAmount(transaction.quantity, '')}
                  </div>
                );
              case 'SPLIT':
                return <div className="text-sm text-purple-600 dark:text-purple-400 font-medium">Stock split ×{transaction.quantity}</div>;
              default:
                return (
                  <>
                    <div className="text-sm text-gray-900 dark:text-gray-100 font-medium">
                      {formatPrice(totalValue)}
                    </div>
                    <div className="text-sm text-gray-400">→</div>
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      {formatAmount(transaction.quantity, '')} @ {formatPrice(transaction.price)}
                    </div>
                  </>
                );
            }
          })()}
        </div>

                {/* Description and Date */}
        <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center space-x-2">
          {locationLabel && (
            <span className="truncate max-w-40 text-purple-600 dark:text-purple-400" title={locationLabel}>
              {locationLabel}
            </span>
          )}
          {transaction.notes && (
            <span className="truncate max-w-64">{transaction.notes}</span>
          )}
          <span>&nbsp;</span>
          <FailedTransactionWarning transaction={transaction} />
          <span>{formatDate(transaction.timestamp)}</span>
        </div>
      </div>

      {/* Right-side actions */}
      <div className="flex items-center space-x-3 ml-4">
        <button
          onClick={handleCopy}
          className="text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100 p-1"
          title="Copy transaction summary"
          aria-label="Copy transaction summary"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onEdit?.(transaction); }}
          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 p-1"
          title="Edit transaction"
          aria-label="Edit transaction"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
          </svg>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete?.(transaction); }}
          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-1"
          title="Delete transaction"
          aria-label="Delete transaction"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
          </svg>
        </button>
      </div>
      </div>
    </>
  );
};

export default TransactionListItem; 